// Action Queue redesign — presentation helpers + atoms.
//
// Ports the Claude Design "Action Queue" mockup (humanized values, colored
// platform marks, inline change formatter) onto the real Proposal model from
// `proposals-api.ts`. The design leaked raw API field names
// (`daily_budget_micros: 30000000`); these helpers turn them into readable
// money/status/ratio strings. Currency is EUR and micro-amounts are divided
// by 1e6, matching the design's `humanizeValue`.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CSSProperties } from 'react';
import type {
  ClientReportProposalMeta,
  Proposal,
} from '../../../lib/api/proposals/proposals-api';

// ---------- client-report proposals ----------

/** True when this proposal is a client-report review item (Change 2). */
export function isClientReportProposal(p: Proposal): boolean {
  return p.execution_type === 'client_report';
}

/** Typed view over a client_report proposal's `metadata`. */
export function clientReportMeta(p: Proposal): ClientReportProposalMeta {
  return (p.metadata as ClientReportProposalMeta) || {};
}

/** Report title for a client_report proposal (entity_name is the title too). */
export function reportTitle(p: Proposal): string {
  return clientReportMeta(p).title || p.entity_name || 'Client report';
}

/** The workbench deep-link, e.g. "/client-reports/<id>". Null when absent. */
export function reportReviewPath(p: Proposal): string | null {
  const path = clientReportMeta(p).report_review_path;
  return typeof path === 'string' && path ? path : null;
}

// ---------- entity, failure reason, auto-approval ----------

// The fields these helpers read; both proposal shapes (queue and history)
// satisfy it.
export interface ProposalDetailFields {
  entity_id?: string | number | null;
  entity_name?: string | null;
  status?: string;
  execution_error?: string | null;
  auto_approved?: boolean | null;
  auto_approval_rule_id?: string | null;
  rule_name?: string | null;
}

const trimmed = (value: unknown): string =>
  value == null ? '' : String(value).trim();

/**
 * What to show for the thing a proposal changes: its name with the id as
 * secondary text, or the id alone when the platform returned no name.
 */
export function proposalEntity(p: ProposalDetailFields): {
  primary: string;
  secondary: string | null;
} {
  const name = trimmed(p.entity_name);
  const id = trimmed(p.entity_id);
  if (!name) return { primary: id, secondary: null };
  return { primary: name, secondary: id && id !== name ? id : null };
}

/**
 * Null unless the proposal failed. For a failed one, the stored reason, or
 * `reason: null` when none was recorded.
 */
export function proposalFailure(
  p: ProposalDetailFields
): { reason: string | null } | null {
  if (p.status !== 'failed') return null;
  return { reason: trimmed(p.execution_error) || null };
}

/** Approved by a rule: the flag is set or the proposal names its rule. */
export function isAutoApproved(p: ProposalDetailFields): boolean {
  return p.auto_approved === true || trimmed(p.auto_approval_rule_id) !== '';
}

/** The name of the rule that approved the proposal, when known. */
export function autoApprovalRuleName(p: ProposalDetailFields): string | null {
  return isAutoApproved(p) ? trimmed(p.rule_name) || null : null;
}

export const FAILURE_REASON_PREVIEW_CHARS = 240;

/** The first `max` characters of a long text, and whether it was cut. */
export function previewText(
  text: string,
  max: number = FAILURE_REASON_PREVIEW_CHARS
): { text: string; truncated: boolean } {
  if (text.length <= max) return { text, truncated: false };
  return {
    text: `${text.slice(0, max).trimEnd()}${ELLIPSIS}`,
    truncated: true,
  };
}

/**
 * The failure reason of a failed proposal as plain wrapped text. A long
 * reason starts shortened, with a control to read all of it.
 */
export function FailureReason({
  reason,
  t,
}: {
  reason: string | null;
  t: (key: string) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!reason) {
    return (
      <span className='aq-failure aq-failure--none'>
        {t('ProposalNoFailureReason')}
      </span>
    );
  }
  const preview = previewText(reason);
  return (
    <span className='aq-failure'>
      <span className='aq-failure__text'>
        {expanded ? reason : preview.text}
      </span>
      {preview.truncated && (
        <button
          type='button'
          className='aq-failure__toggle'
          aria-expanded={expanded}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? t('ProposalReasonShowLess') : t('ProposalReasonShowAll')}
        </button>
      )}
    </span>
  );
}

/**
 * Markers next to a proposal's title: "Failed" (the reason as its tooltip)
 * and "Auto-approved" (with the rule name when the proposal carries one).
 */
export function ProposalMarkers({
  proposal,
  t,
  showFailed = true,
}: {
  proposal: ProposalDetailFields;
  t: (key: string) => string;
  showFailed?: boolean;
}) {
  const failure = showFailed ? proposalFailure(proposal) : null;
  const auto = isAutoApproved(proposal);
  const rule = autoApprovalRuleName(proposal);
  if (!failure && !auto) return null;
  return (
    <>
      {failure && (
        <span
          className='aq-marker aq-marker--failed'
          title={failure.reason || t('ProposalNoFailureReason')}
        >
          {t('Failed')}
        </span>
      )}
      {auto && (
        <span
          className='aq-marker aq-marker--auto'
          title={rule ? `${t('Rule')}: ${rule}` : undefined}
        >
          {rule ? `${t('AutoApproved')} (${rule})` : t('AutoApproved')}
        </span>
      )}
    </>
  );
}

// ---------- value humanization ----------

export interface HumanizedValue {
  text: string;
  unit?: string;
  status?: string;
}

const EM_DASH = '—';
const EURO = '€';
const ELLIPSIS = '…';

export function EUR(n: number): string {
  return (
    EURO +
    Number(n).toLocaleString('en-US', {
      minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
      maximumFractionDigits: 2,
    })
  );
}

// Turn a single-key value object ({ daily_budget_micros: 30000000 }) into a
// friendly { text, unit } pair. Mirrors the design's humanizeValue.
export function humanizeValue(
  value: Record<string, any> | null | undefined
): HumanizedValue {
  if (value == null) return { text: EM_DASH };
  const keys = Object.keys(value);
  if (keys.length === 0) return { text: EM_DASH };
  const k = keys[0];
  const v = value[k];
  switch (k) {
    case 'daily_budget_micros':
    case 'lifetime_budget_micros':
    case 'bid_micros':
    case 'target_cpa_micros':
      return {
        text: EUR(Number(v) / 1000000),
        unit:
          k.indexOf('lifetime') >= 0
            ? 'lifetime'
            : k.indexOf('bid') >= 0
            ? 'bid'
            : k.indexOf('cpa') >= 0
            ? 'CPA'
            : '/day',
      };
    case 'budget':
    case 'daily_budget':
      return { text: EUR(Number(v)), unit: '/day' };
    case 'lifetime_budget':
      return { text: EUR(Number(v)), unit: 'lifetime' };
    case 'bid_amount':
      return { text: EUR(Number(v)), unit: 'bid' };
    case 'status': {
      const s = String(v).toLowerCase();
      return { text: s.charAt(0).toUpperCase() + s.slice(1), status: s };
    }
    case 'target_roas':
      return { text: Number(v).toFixed(1) + 'x', unit: 'ROAS' };
    default:
      return { text: v == null ? EM_DASH : String(v) };
  }
}

// Short label describing WHICH metric is changing, derived from the value key.
export function changeMetric(
  value: Record<string, any> | null | undefined
): string {
  const k = value ? Object.keys(value)[0] || '' : '';
  const map: Record<string, string> = {
    daily_budget_micros: 'Daily budget',
    daily_budget: 'Daily budget',
    budget: 'Daily budget',
    lifetime_budget: 'Lifetime budget',
    lifetime_budget_micros: 'Lifetime budget',
    bid_micros: 'Bid',
    bid_amount: 'Bid',
    status: 'Status',
    target_roas: 'Target ROAS',
    target_cpa_micros: 'Target CPA',
  };
  return map[k] || (k ? k.replace(/_/g, ' ') : 'Value');
}

// ---------- titles / time ----------

export function titleCase(s: string): string {
  return s.replace(
    /\w\S*/g,
    (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
  );
}

// The real model has no `action_title`. Derive a human title from the
// action type, then the tool name, then the category, falling back to a
// translated "Action".
export function proposalActionTitle(
  p: Proposal,
  t: (key: string) => string
): string {
  if (p.action_type?.name) return p.action_type.name;
  if (p.tool_name) return titleCase(p.tool_name.replace(/_/g, ' '));
  if (p.category) return titleCase(p.category.replace(/_/g, ' '));
  return t('Action');
}

export function shopLabel(p: Proposal): string {
  return p.shop?.name || p.shop?.slug || `Shop #${p.shop_id}`;
}

// Compact relative time: now / 5m / 3h / 2d / "Jan 4".
export function timeAgo(iso: string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '';
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  if (h < 24) return `${h}h`;
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

// ---------- risk ----------

const RISK_TONE: Record<string, string> = {
  low: 'green',
  medium: 'amber',
  high: 'orange',
  critical: 'red',
};

const RISK_LABEL: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

export function riskTone(level: string): string {
  return RISK_TONE[level] || 'gray';
}

// ---------- platform metadata ----------

export interface PlatformMeta {
  name: string;
  short: string;
  glyph: string;
  color: string;
  mono: string;
  // Glyph colour when white text would not read on the tile colour.
  fg?: string;
  tiktok?: boolean;
}

const PLATFORM_META: Record<string, PlatformMeta> = {
  facebook_ads: {
    name: 'Facebook Ads',
    short: 'Meta',
    glyph: 'f',
    color: '#1877F2',
    mono: 'FB',
  },
  google_ads: {
    name: 'Google Ads',
    short: 'Google',
    glyph: 'G',
    color: '#1A73E8',
    mono: 'GA',
  },
  tiktok_ads: {
    name: 'TikTok Ads',
    short: 'TikTok',
    glyph: '♪',
    color: '#111111',
    mono: 'TT',
    tiktok: true,
  },
  linkedin_ads: {
    name: 'LinkedIn Ads',
    short: 'LinkedIn',
    glyph: 'in',
    color: '#0A66C2',
    mono: 'LI',
  },
  snapchat_ads: {
    name: 'Snapchat Ads',
    short: 'Snapchat',
    glyph: 'S',
    color: '#FFFC00',
    mono: 'SC',
    fg: '#111111',
  },
};

// The ad platforms proposals can target, in display order. Shared by the
// Action Center filters and the rule editor so the lists cannot drift.
export const PROPOSAL_PLATFORM_OPTIONS: { value: string; label: string }[] =
  Object.entries(PLATFORM_META).map(([value, meta]) => ({
    value,
    label: meta.name,
  }));

// Always returns a usable meta; unknown platforms get a neutral mark derived
// from the slug so the row never renders blank.
export function platformMeta(platform: string): PlatformMeta {
  const m = PLATFORM_META[platform];
  if (m) return m;
  const label = titleCase((platform || '').replace(/_/g, ' ')) || 'Platform';
  const short = label.replace(/\s*Ads$/i, '') || label;
  const glyph = (platform || '?').charAt(0).toUpperCase();
  return { name: label, short, glyph, color: '#6b7280', mono: glyph };
}

// ---------- change direction ----------

export type DeltaTone = 'up' | 'down' | 'neutral';

// Pausing reads as caution (down); activating as positive (up). Otherwise
// follow the sign of change_percentage. Status comparison is case-insensitive
// because the API may return ACTIVE/PAUSED in either case.
export function deltaTone(proposal: Proposal): DeltaTone {
  const next = proposal.proposed_value || {};
  if (next.status !== undefined) {
    return String(next.status).toUpperCase() === 'PAUSED' ? 'down' : 'up';
  }
  const pct = proposal.change_percentage;
  if (pct == null) return 'neutral';
  return pct > 0 ? 'up' : pct < 0 ? 'down' : 'neutral';
}

function statusTone(s: any): 'paused' | 'active' {
  return String(s).toUpperCase() === 'PAUSED' ? 'paused' : 'active';
}

export function countNoun(p: Proposal): string {
  const hay = `${p.tool_name || ''} ${p.category || ''} ${
    p.entity_name || ''
  }`.toLowerCase();
  return /keyword/.test(hay) ? 'keywords' : '';
}

export function formatPct(pct: number): string {
  return `${pct > 0 ? '+' : ''}${pct.toFixed(pct % 1 === 0 ? 0 : 1)}%`;
}

// ---------- icon glyphs ----------

const ICON: Record<string, string> = {
  arrowRight: 'M5 12h14M12 5l7 7-7 7',
  check: 'M20 6L9 17l-5-5',
  x: 'M18 6L6 18M6 6l12 12',
  info: 'M12 16v-4M12 8h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z',
  clock: 'M12 7v5l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0',
  // Phase 4 — batch card / creative previews.
  chevronDown: 'M6 9l6 6 6-6',
  play: 'M7 4l13 8-13 8V4z',
  image:
    'M3 5h18v14H3V5zM8.5 11a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM21 15l-5-5-8 8',
  // Change 2 — client-report proposals.
  file: 'M14 3v5h5M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V8l-4-5zM9 13h6M9 17h6',
  externalLink:
    'M15 3h6v6M10 14L21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6',
  alertTriangle:
    'M12 9v4M12 17h.01M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z',
};

export function Glyph({
  name,
  size = 16,
  stroke = 1.8,
  className,
}: {
  name: keyof typeof ICON | string;
  size?: number;
  stroke?: number;
  className?: string;
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth={stroke}
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d={ICON[name] || ''} />
    </svg>
  );
}

// ---------- presentational atoms ----------

export function PlatformMark({
  platform,
  size = 36,
}: {
  platform: string;
  size?: number;
}) {
  const meta = platformMeta(platform);
  const style: CSSProperties = {
    width: size,
    height: size,
    borderRadius: Math.round(size * 0.28),
    background: meta.color,
    color: meta.fg || '#fff',
    fontSize: size * (meta.glyph.length > 1 ? 0.4 : 0.52),
  };
  // TikTok gets its signature cyan/magenta split edge on a near-black tile.
  if (meta.tiktok) {
    style.background = '#111111';
    style.boxShadow = 'inset 1.5px 0 0 #25F4EE, inset -1.5px 0 0 #FE2C55';
  }
  return (
    <span className='aq-pmark' style={style} title={meta.name}>
      {meta.glyph}
    </span>
  );
}

// Change 2 — a report tile used in place of the platform mark for
// client_report proposals (which have no ad platform).
export function ReportMark({ size = 36 }: { size?: number }) {
  const style: CSSProperties = {
    width: size,
    height: size,
    borderRadius: Math.round(size * 0.28),
    background: '#6b4eff',
    color: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  return (
    <span className='aq-pmark' style={style} title='Client report review'>
      <Glyph name='file' size={Math.round(size * 0.5)} />
    </span>
  );
}

/** Still waiting for a decision: the only statuses that can be approved or denied. */
export function isAwaitingReview(status: string): boolean {
  return status === 'pending_review' || status === 'proposed';
}

/**
 * The risk level as a coloured dot. `muted` greys it out for a proposal that
 * is no longer waiting (denied, expired, completed, ...), so the colour does
 * not read as something that still needs attention; the level stays in the
 * tooltip and the accessible name.
 */
export function RiskDot({
  level,
  muted = false,
}: {
  level: string;
  muted?: boolean;
}) {
  const label = `${RISK_LABEL[level] || level} risk`;
  return (
    <span
      className={`aq-riskdot aq-riskdot--${muted ? 'muted' : riskTone(level)}`}
      role='img'
      title={label}
      aria-label={label}
      data-testid='aq-riskdot'
    />
  );
}

export function ChangeInline({
  proposal,
  size = 'md',
}: {
  proposal: Proposal;
  size?: 'md' | 'lg';
}) {
  const curObj = proposal.current_value || {};
  const nextObj = proposal.proposed_value || {};
  const cur = humanizeValue(curObj);
  const next = humanizeValue(nextObj);
  const pct = proposal.change_percentage;
  const tone = deltaTone(proposal);
  const isStatus = nextObj.status !== undefined;
  const isCount = curObj.count !== undefined || nextObj.count !== undefined;
  const noun = countNoun(proposal);
  const curUnknown = isCurrentValueUnknown(proposal);

  return (
    <span className={`aq-chg aq-chg--${size}`}>
      {isCount ? (
        <span className='aq-chg__count'>
          +{Number(nextObj.count)}
          {noun ? ` ${noun}` : ''}
        </span>
      ) : (
        <>
          {curUnknown ? (
            <UnknownCurrentValue size={size} />
          ) : (
            <span
              className={`aq-chg__val aq-chg__val--${
                isStatus ? statusTone(curObj.status) : 'cur'
              }`}
            >
              {cur.text}
            </span>
          )}
          <Glyph
            name='arrowRight'
            size={size === 'lg' ? 16 : 13}
            className='aq-chg__arrow'
          />
          <span
            className={`aq-chg__val aq-chg__val--${
              isStatus ? statusTone(nextObj.status) : 'next'
            }`}
          >
            {next.text}
          </span>
          {next.unit && <span className='aq-chg__unit'>{next.unit}</span>}
        </>
      )}
      {pct != null && (
        <span className={`aq-chg__pct aq-chg__pct--${tone}`}>
          {formatPct(pct)}
        </span>
      )}
    </span>
  );
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// The owning employee, resolved to a name by the page (UUID -> name map). When
// the name is unknown (map miss / no requester), fall back to a person glyph.
export function RequesterAvatar({
  name,
  size = 22,
}: {
  name?: string;
  size?: number;
}) {
  const initials = name ? initialsFromName(name) : '';
  const style: CSSProperties = {
    width: size,
    height: size,
    fontSize: size * 0.42,
  };
  return (
    <span className='aq-req__avatar' style={style} title={name || undefined}>
      {initials || <Glyph name='user' size={Math.round(size * 0.6)} />}
    </span>
  );
}

/** True for action keys that create a new entity rather than change one. */
export function isCreateActionKey(actionKey?: string | null): boolean {
  if (!actionKey) return false;
  const key = actionKey.toLowerCase();
  const last = key.split('.').pop() || '';
  return (
    last === 'create' ||
    last.startsWith('create_') ||
    last.endsWith('_create') ||
    /(^|[._])create_ad/.test(key) ||
    key.includes('bulk_launch')
  );
}

/**
 * True when a proposal that changes an existing entity carries no before
 * value (the platform could not be read when it was filed). Client report
 * reviews, creates and count proposals (add N keywords) never have one.
 */
export function isCurrentValueUnknown(proposal: {
  current_value?: unknown;
  proposed_value?: Record<string, any> | null;
  execution_type?: string | null;
  action_key?: string | null;
}): boolean {
  const cur = proposal.current_value;
  const next = proposal.proposed_value || {};
  if (next.count !== undefined) return false;
  if (proposal.execution_type === 'client_report') return false;
  if (isCreateActionKey(proposal.action_key)) return false;
  // 0 and false are real before values; only missing, blank or empty is
  // unknown.
  if (cur == null || cur === '') return true;
  if (typeof cur === 'object') return Object.keys(cur).length === 0;
  return false;
}

/**
 * Shown in place of a bare dash when the current value is unknown: a warning
 * marker, a short label, and a tooltip asking to check the ad platform.
 */
export function UnknownCurrentValue({ size = 'md' }: { size?: 'md' | 'lg' }) {
  const { t } = useTranslation();
  const label = t('ProposalCurrentValueUnknown');
  const hint = t('ProposalCurrentValueUnknownHint');
  return (
    <span
      className={`aq-unknown-cur aq-unknown-cur--${size}`}
      title={hint}
      aria-label={`${label}. ${hint}`}
      role='note'
      data-testid='aq-unknown-cur'
    >
      <Glyph
        name='alertTriangle'
        size={size === 'lg' ? 16 : 13}
        className='aq-unknown-cur__icon'
      />
      <span>{label}</span>
    </span>
  );
}
